'use client';

import { useState, useRef, useEffect, forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { dropdownVariants } from '@/lib/animations';

interface SelectOption {
    value: string;
    label: string;
    searchText?: string; // Optional text to search against (includes label if not provided)
}

interface SelectProps {
    label?: string;
    error?: string;
    options: SelectOption[];
    placeholder?: string;
    value?: string;
    onChange?: (value: string) => void;
    onBlur?: (e?: any) => void;
    disabled?: boolean;
    className?: string;
    id?: string;
    name?: string;
    size?: 'default' | 'sm';
    actionIcon?: {
        icon: React.ReactNode;
        onClick: () => void;
        title?: string;
    };
}

export const Select = forwardRef<HTMLDivElement, SelectProps>(function Select({
    label,
    error,
    options,
    placeholder,
    value,
    onChange,
    onBlur,
    disabled,
    className = '',
    id,
    name,
    size = 'default',
    actionIcon,
}, ref) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const internalRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const selectId = id || name;

    const selectedOption = options.find(opt => opt.value === value);

    // Filter options based on search query (use searchText if provided, otherwise label)
    const filteredOptions = searchQuery
        ? options.filter(opt => {
            const searchTarget = (opt.searchText || opt.label).toLowerCase();
            return searchTarget.includes(searchQuery.toLowerCase());
        })
        : options;

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (internalRef.current && !internalRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchQuery('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    // Close on escape
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
                setSearchQuery('');
                inputRef.current?.blur();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    const handleSelect = (optionValue: string) => {
        if (onChange) {
            onChange(optionValue);
        }
        if (onBlur) {
            onBlur();
        }
        setIsOpen(false);
        setSearchQuery('');
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        if (!isOpen) {
            setIsOpen(true);
        }
        setHoveredIndex(0);
    };

    const handleInputFocus = () => {
        setIsOpen(true);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHoveredIndex(prev =>
                prev === null ? 0 : Math.min(prev + 1, filteredOptions.length - 1)
            );
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHoveredIndex(prev =>
                prev === null ? 0 : Math.max(prev - 1, 0)
            );
        } else if (e.key === 'Enter' && hoveredIndex !== null && filteredOptions[hoveredIndex]) {
            e.preventDefault();
            handleSelect(filteredOptions[hoveredIndex].value);
        }
    };

    return (
        <div className={`w-full ${className}`} ref={internalRef}>
            {label && (
                <label
                    htmlFor={selectId}
                    className="block text-xs font-medium text-slate-500 mb-1"
                >
                    {label}
                </label>
            )}
            <div className="relative">
                <div
                    className={`
                        w-full ${size === 'sm' ? 'px-3 py-2 text-sm' : 'px-4 py-3'}
                        bg-white
                        border ${isOpen ? 'border-black' : 'border-slate-300'}
                        transition-all duration-200
                        hover:border-slate-400
                        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                        ${error ? 'border-red-500' : ''}
                        flex items-center justify-between
                    `}
                >
                    <input
                        ref={inputRef}
                        type="text"
                        id={selectId}
                        value={searchQuery || (isOpen ? '' : selectedOption?.label || '')}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onKeyDown={handleKeyDown}
                        placeholder={selectedOption?.label || placeholder || 'Select...'}
                        disabled={disabled}
                        autoComplete="off"
                        className={`
                            flex-1 bg-transparent outline-none min-w-0 text-sm
                            ${selectedOption && !isOpen ? 'text-black' : 'text-slate-800'}
                            placeholder:text-slate-400
                        `}
                    />
                    {actionIcon && value && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                actionIcon.onClick();
                            }}
                            className="flex-shrink-0 ml-2 p-1 text-black hover:text-slate-700 transition-colors cursor-pointer"
                            title={actionIcon.title}
                        >
                            {actionIcon.icon}
                        </button>
                    )}
                    <motion.svg
                        className="w-5 h-5 text-slate-500 flex-shrink-0 ml-2 cursor-pointer"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={(e) => {
                            e.preventDefault();
                            if (isOpen) {
                                setIsOpen(false);
                            } else {
                                setIsOpen(true);
                                inputRef.current?.focus();
                            }
                        }}
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 9l-7 7-7-7" />
                    </motion.svg>
                </div>

                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            className="absolute z-50 w-full mt-0 bg-white border border-slate-300 border-t-0 max-h-60 overflow-y-auto"
                            variants={dropdownVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                        >
                            <div className="py-1">
                                {filteredOptions.length === 0 ? (
                                    <div className="px-3 py-2 text-sm text-slate-400">
                                        No results found
                                    </div>
                                ) : (
                                    filteredOptions.map((option, index) => (
                                        <button
                                            key={`${option.value}-${index}`}
                                            type="button"
                                            onClick={() => handleSelect(option.value)}
                                            onMouseEnter={() => setHoveredIndex(index)}
                                            onMouseLeave={() => setHoveredIndex(null)}
                                            className={`
                                                w-full px-3 py-2 text-left text-sm
                                                transition-colors duration-150
                                                ${option.value === value
                                                    ? 'bg-slate-100 text-black font-medium'
                                                    : hoveredIndex === index
                                                        ? 'bg-slate-50 text-black'
                                                        : 'text-slate-600'
                                                }
                                                flex items-center justify-between
                                            `}
                                        >
                                            <span>{option.label}</span>
                                        </button>
                                    ))
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
            {error && (
                <p className="mt-1.5 text-sm text-red-500">{error}</p>
            )}
        </div>
    );
});
